import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {NdcSpreadsheetImportSubmissionValidationErrors} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert} from 'Asserts';
import {ERRORS_TABLE_PAGINATION_LIMIT} from 'bulk-import/SpreadsheetBulkImportConstants';
import ResultsPager from 'ui-library/ResultsPager';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {formatRows} from 'util/ExportUtil';

// We add 2 to the displayed rowIndex because errors are returned 0-indexed and the backend returns indexes
// relative to the data rows, not the header row.
const ROW_INDEX_DISPLAY_OFFSET = 2;

export interface DisplayValidationError {
  rowNumber: string;
  errorMessage: string;
}

const useStyles = createStyles(() => ({
  copyErrorsContainer: {
    marginLeft: 'auto',
  },
  rowNumberCell: {
    whiteSpace: 'nowrap',
    width: '1%',
    // Double class selector with && (e.g. `.mantine-abc123.mantine-abc123`) to beat bootstrap specificity
    '&&': {
      verticalAlign: 'middle',
    },
  },
  messageCell: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
}));

export function getTSVErrors(displayErrors: Array<DisplayValidationError>): string {
  const rows = [['Row #', 'Message']];
  for (const error of displayErrors) {
    rows.push([error.rowNumber, error.errorMessage]);
  }
  return formatRows(rows, '\t');
}

export interface ValidationErrorsPaneProps {
  submissionValidationErrors: NdcSpreadsheetImportSubmissionValidationErrors;
}

export default function ValidationErrorsPane({
  submissionValidationErrors,
}: ValidationErrorsPaneProps): JSX.Element {
  const {validationErrors, batchErrors} = submissionValidationErrors;
  assert(
    validationErrors.length > 0 || batchErrors.length > 0,
    'ValidationErrorsPane requires at least one submission validation error'
  );

  const {classes} = useStyles();
  const [pageOffset, setOffset] = useState<number>(0);
  const displayErrors = useMemo<Array<DisplayValidationError>>(
    () => [
      ...batchErrors.map((errorMessage) => ({
        rowNumber: '',
        errorMessage,
      })),
      ...validationErrors.map((error) => ({
        rowNumber: `${error.rowIndex + ROW_INDEX_DISPLAY_OFFSET}`,
        errorMessage: error.errorMessage,
      })),
    ],
    [batchErrors, validationErrors]
  );
  const tsvErrors = useMemo(() => getTSVErrors(displayErrors), [displayErrors]);
  const pageErrors = displayErrors.slice(pageOffset, pageOffset + ERRORS_TABLE_PAGINATION_LIMIT);

  const handleCopyErrors = (): void => {
    copyToClipboard(tsvErrors, {
      showToastOnSuccess: true,
      toastText: 'Copied errors to clipboard.',
    });
  };

  return (
    <div>
      <table
        className="table"
        {...getTestProps({component: 'ValidationErrorsPane', element: 'validationErrorsTable'})}
      >
        <thead>
          <tr>
            <th className={classes.rowNumberCell}>Row #</th>
            <th>
              <div className="u-vertical-center-items">
                Message
                <div className={classes.copyErrorsContainer}>
                  <Button variant="ghost" compact={true} onClick={handleCopyErrors}>
                    Copy errors
                  </Button>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {pageErrors.map((error, index) => (
            <tr key={pageOffset + index}>
              <td className={`bulk-import--column-text-column ${classes.rowNumberCell}`}>
                <span className="bulk-import--table-text">{error.rowNumber}</span>
              </td>
              <td className={`bulk-import--column-text-column ${classes.messageCell}`}>
                <span className="bulk-import--table-text">{error.errorMessage}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {displayErrors.length > ERRORS_TABLE_PAGINATION_LIMIT && (
        <ResultsPager
          setOffset={setOffset}
          pagination={{
            offset: pageOffset,
            limit: ERRORS_TABLE_PAGINATION_LIMIT,
            total: displayErrors.length,
          }}
          isLoading={false}
        />
      )}
    </div>
  );
}
